/**
 * 地图样式加载器，支持失败重试
 */
export class MapStyleLoader {
  private mapInstance: any
  private styleEventHandlers: {
    success?: (type: any, target: any) => void
    error?: (type: any, target: any) => void
    timeout?: (type: any, target: any) => void
  } = {}
  private retryCount = 0
  private maxRetries: number
  private styleJson: Array<any>
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private isHandlingFailure = false // 标记是否正在处理失败事件，防止error和timeout重复触发

  constructor(mapInstance: any, styleJson: Array<any>, maxRetries = 3) {
    this.mapInstance = mapInstance
    this.styleJson = styleJson
    this.maxRetries = maxRetries
  }

  /**
   * 清理事件监听器
   */
  private cleanup(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }

    // 重置处理标志
    this.isHandlingFailure = false

    if (this.styleEventHandlers.success) {
      this.mapInstance.removeEventListener('onstyle_loaded', this.styleEventHandlers.success)
      this.styleEventHandlers.success = undefined
    }
    if (this.styleEventHandlers.error) {
      this.mapInstance.removeEventListener('onstyle_loaded_error', this.styleEventHandlers.error)
      this.styleEventHandlers.error = undefined
    }
    if (this.styleEventHandlers.timeout) {
      this.mapInstance.removeEventListener(
        'onstyle_loaded_timeout',
        this.styleEventHandlers.timeout
      )
      this.styleEventHandlers.timeout = undefined
    }
  }

  /**
   * 执行重试
   */
  private retry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer)
    // 重置处理标志，允许下一次加载尝试处理失败事件
    this.isHandlingFailure = false
    this.retryTimer = setTimeout(() => {
      this.mapInstance.setMapStyleV2({
        styleJson: this.styleJson,
      })
    }, 1000)
  }

  /**
   * 处理加载失败（error或timeout）
   */
  private handleFailure(eventType: 'error' | 'timeout', type: any, target: any): void {
    // 如果已经在处理失败事件，忽略后续的重复事件
    if (this.isHandlingFailure) {
      return
    }

    this.isHandlingFailure = true
    const eventName = eventType === 'error' ? '失败' : '超时'
    console.log(`地图自定义样式${eventName}！`, type, target)

    this.retryCount++
    if (this.retryCount < this.maxRetries) {
      console.log(`地图样式加载${eventName}，正在重试 (${this.retryCount}/${this.maxRetries})...`)
      this.retry()
    } else {
      console.error(`地图样式加载${eventName}，已达到最大重试次数 (${this.maxRetries})`)
      this.cleanup()
    }
  }

  /**
   * 加载地图样式
   */
  load(): void {
    if (!this.mapInstance) return

    // 重置重试计数和状态
    this.retryCount = 0
    this.isHandlingFailure = false

    // 先清理之前的监听器
    this.cleanup()

    // 成功回调
    this.styleEventHandlers.success = (type: any, target: any) => {
      console.log('地图自定义样式已成功加载并应用！', type, target)
      this.isHandlingFailure = false
      this.cleanup()
    }

    // 失败回调
    this.styleEventHandlers.error = (type: any, target: any) => {
      this.handleFailure('error', type, target)
    }

    // 超时回调
    this.styleEventHandlers.timeout = (type: any, target: any) => {
      this.handleFailure('timeout', type, target)
    }

    // 注册事件监听器
    this.mapInstance.addEventListener('onstyle_loaded', this.styleEventHandlers.success)
    this.mapInstance.addEventListener('onstyle_loaded_error', this.styleEventHandlers.error)
    this.mapInstance.addEventListener('onstyle_loaded_timeout', this.styleEventHandlers.timeout)

    // 首次设置样式
    this.mapInstance.setMapStyleV2({
      styleJson: this.styleJson,
    })
  }

  /**
   * 销毁实例，清理所有资源
   */
  destroy(): void {
    this.cleanup()
  }
}
